import { useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Action, RemindingTone } from '../api/gql';
import { SelectCard } from './input/SelectCard';
import { ActionIcon } from './invoice/ScheduledActionIcon';

type Props = {
  onToneChange: (remindingTone: RemindingTone) => void;
  selected?: RemindingTone;
  remindingToneLoading?: RemindingTone;
};

export const RemindingTones = ({ onToneChange, remindingToneLoading, selected }: Props) => {
  const { t } = useTranslation();

  const onClick = useCallback(
    (remindingTone: RemindingTone) => () => {
      onToneChange(remindingTone);
    },
    [onToneChange],
  );

  return (
    <Box
      display={'flex'}
      flexDirection={'row'}
      justifyContent={'center'}
      alignItems={'stretch'}
      flexWrap={'wrap'}
      gap={6}
    >
      <SelectCard
        title={t('tone.polite.title')}
        description={t('tone.polite.description')}
        onClick={onClick(RemindingTone.Polite)}
        selected={selected === RemindingTone.Polite}
        actionArea={<SMSExample text={t('tone.polite.example')} selected={selected === RemindingTone.Polite} />}
        loading={remindingToneLoading === RemindingTone.Polite}
      />
      <SelectCard
        title={t('tone.neutral.title')}
        description={t('tone.neutral.description')}
        onClick={onClick(RemindingTone.Neutral)}
        selected={selected === RemindingTone.Neutral}
        actionArea={<SMSExample text={t('tone.neutral.example')} selected={selected === RemindingTone.Neutral} />}
        loading={remindingToneLoading === RemindingTone.Neutral}
      />
      <SelectCard
        title={t('tone.firm.title')}
        description={t('tone.firm.description')}
        onClick={onClick(RemindingTone.Firm)}
        selected={selected === RemindingTone.Firm}
        actionArea={<SMSExample text={t('tone.firm.example')} selected={selected === RemindingTone.Firm} />}
        loading={remindingToneLoading === RemindingTone.Firm}
      />
    </Box>
  );
};

type SMSExampleProps = {
  text: string;
  selected?: boolean;
};
const SMSExample = ({ text, selected }: SMSExampleProps) => {
  const { t } = useTranslation();

  return (
    <Box display={'flex'} flexDirection={'column'} alignItems={'center'} mt={4}>
      <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap={2}>
        <ActionIcon action={Action.Sms} color={'action'} fontSize={'small'} />
        <Typography gutterBottom variant={'overline'}>
          {t('smsExample')}
        </Typography>
      </Box>
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: 'background.default',
          borderRadius: 1,
          textAlign: 'left',
          flex: 1,
        }}
      >
        <Typography variant={'body2'}>{text}</Typography>
      </Box>
      <Box display={'flex'} justifyContent={'center'} mt={2}>
        <Button component={'div'} disabled={selected} variant={'contained'}>
          {t('select')}
        </Button>
      </Box>
    </Box>
  );
};
